/**
 * SignatureCanvas — riquadro firma disegnata (★ Fase 8).
 * Pointer events (mouse + touch), nessuna dipendenza esterna.
 */
import React, { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { Eraser } from 'lucide-react';

export const SignatureCanvas = forwardRef(function SignatureCanvas({ onChange }, ref) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    const dpr = window.devicePixelRatio || 1;
    const width = canvas.parentElement.clientWidth;
    const height = 180;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0C3C2D';
  }, []);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    canvasRef.current.setPointerCapture?.(e.pointerId);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    if (isEmpty) {
      setIsEmpty(false);
      onChange?.(false);
    }
  };

  const end = () => { drawing.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setIsEmpty(true);
    onChange?.(true);
  };

  useImperativeHandle(ref, () => ({
    isEmpty: () => isEmpty,
    toDataURL: () => canvasRef.current.toDataURL('image/png'),
    clear,
  }), [isEmpty]);

  return (
    <div className="relative">
      <canvas
        ref={canvasRef}
        className="w-full rounded-lg border-2 border-dashed border-[var(--gp-forest,#0C3C2D)]/30 bg-white touch-none cursor-crosshair"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        data-testid="signature-canvas"
      />
      {isEmpty && (
        <span className="absolute inset-0 grid place-items-center text-sm text-slate-400 pointer-events-none">
          Firma qui con il dito o il mouse
        </span>
      )}
      <button
        type="button"
        onClick={clear}
        className="absolute top-2 right-2 p-1.5 rounded-md bg-white/90 border border-slate-200 text-slate-500 hover:text-slate-800"
        title="Cancella firma"
        data-testid="signature-clear"
      >
        <Eraser className="w-4 h-4" />
      </button>
    </div>
  );
});
